import { Switch, Route, Router, Redirect } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { useEffect, useRef, useState } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/lib/theme";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { track, trackPageView } from "@/lib/analytics";
import { recordReferralClick, matchReferralClick, isNativeApp } from "@/lib/referralFingerprint";
import { Layout } from "@/components/Layout";
import Dashboard from "@/pages/dashboard";
import Friends from "@/pages/friends";
import Groups from "@/pages/groups";
import GroupDetail from "@/pages/group-detail";
import FriendDetail from "@/pages/friend-detail";
import ActivityPage from "@/pages/activity";
import Admin from "@/pages/admin";
import { LandingGate } from "@/pages/landing";
import AppOnly from "@/pages/app-only";
import { acceptInvite, clearPendingInvite, extractInviteCode, getPendingInvite } from "@/lib/invite-handoff";
import { toast } from "@/hooks/use-toast";
import ResetPassword from "@/pages/reset-password";
import NotFound from "@/pages/not-found";
import Import from "@/pages/import";
import AiMode from "@/pages/ai-mode";
import OnboardingPreferences from "@/pages/onboarding";
import FirstRunWizard from "@/pages/first-run";
import OnboardingV2 from "@/pages/onboarding-v2";
import IntroTour from "@/pages/intro";
import type { FinishAction } from "@/pages/intro/scenes";
import { rememberHeardFrom, syncHeardFrom } from "@/lib/heard-from";
import AuthPage from "@/pages/auth";
import { isStandalonePWA } from "@/pages/landing";
import { isIosNative } from "@/lib/iap";
import { IOS_ONBOARDING_VERSION, compareVersions } from "@shared/appVersions";
import InvitePage from "@/pages/invite";
import { ReviewPromptSheet } from "@/components/ReviewPromptSheet";
import { CUSTOM_REVIEW_SHEET_ENABLED } from "@/lib/reviewPrompt";
import { ForceUpdateGate } from "@/components/ForceUpdateGate";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { isInTWA } from "@/lib/platform";
import { initDeepLinkHandling } from "@/lib/deeplink";
import { configureRevenueCat } from "@/lib/iap";
import { recordFirstLaunch, askForRatingOnOpen } from "@/lib/native-review";
import { PlayRatingCard } from "@/components/PlayRatingCard";
import { syncTrackingStatus, maybeRequestTracking, logMetaRegistration } from "@/lib/meta-attribution";

// Cutover flag for onboarding v2. OFF by default — set VITE_ENABLE_ONBOARDING_V2
// to "true" in the Render environment to turn the new onboarding on for new
// users. While OFF, behaviour is exactly as before (new users get the old
// FirstRunWizard). Build-time flag (Vite), so flipping it triggers a rebuild.
const ENABLE_ONBOARDING_V2 =
  import.meta.env.VITE_ENABLE_ONBOARDING_V2 === "true";

// localStorage key that records the onboarding demo has been shown on this
// device. Set once, on first install — survives logout (logout never clears
// localStorage), so the demo shows exactly ONCE per install and never again.
const ONBOARDING_SEEN_KEY = "spliiit_seen_onboarding";

// The intro tour (Sept 2026) shows once per device, to everyone: new people
// before they sign up, existing members on their next open. Set when they
// finish or skip it, so quitting halfway shows it again next time.
const INTRO_SEEN_KEY = "spliiit_intro_seen";
// Bump when the tour changes. Everyone else still sees it once; the admin sees
// it again after every change, to check it on a real phone.
const INTRO_VERSION = "2";
// While true, the admin sees the tour on every launch of the app (Skip gets
// past it for that launch). Set back to false to return to "once per change".
const ADMIN_SEES_INTRO_EVERY_LAUNCH = true;

/**
 * Whether this install may show the intro. On iOS only from the release that
 * ships it (IOS_ONBOARDING_VERSION), so people see it right after they update;
 * null while the installed version is read (a few ms). Everywhere else: yes.
 */
function useIntroAllowed(): boolean | null {
  const [allowed, setAllowed] = useState<boolean | null>(isIosNative ? null : true);
  useEffect(() => {
    if (!isIosNative) return;
    import("@capacitor/app")
      .then(({ App }) => App.getInfo())
      .then((info) => setAllowed(compareVersions(info.version, IOS_ONBOARDING_VERSION) >= 0))
      .catch(() => setAllowed(false));
  }, []);
  return allowed;
}

function AppRouter() {
  const { user, isLoading, refreshUser } = useAuth();
  const { syncFromDb } = useTheme();
  // Subscribe to hash changes so this component re-renders on navigation.
  // The body below reads window.location.hash directly, but without this
  // subscription AppRouter wouldn't re-run when the hash changes (e.g. when
  // the invite page sets hash="#/" to send a logged-out user to AuthPage).
  useHashLocation();

  const [introSeen, setIntroSeen] = useState<string | null>(() => {
    try { return localStorage.getItem(INTRO_SEEN_KEY); } catch { return "storage-off"; }
  });
  // Finished or skipped during this launch (only matters for the admin).
  const [introDoneThisLaunch, setIntroDoneThisLaunch] = useState(false);
  const introPending = !introSeen || (!!user?.isAdmin &&
    (ADMIN_SEES_INTRO_EVERY_LAUNCH ? !introDoneThisLaunch : introSeen !== INTRO_VERSION));
  const introAllowed = useIntroAllowed();
  // Which sign-in they picked on the intro's last slide (logged out only).
  const [authStart, setAuthStart] = useState<FinishAction | null>(null);
  // Send a "where did you hear about us?" answer waiting from the intro once signed in.
  useEffect(() => { if (user?.id) void syncHeardFrom(); }, [user?.id]);
  const finishIntro = () => {
    try { localStorage.setItem(INTRO_SEEN_KEY, INTRO_VERSION); } catch { /* storage off */ }
    setIntroSeen(INTRO_VERSION);
    setIntroDoneThisLaunch(true);
  };

  // Whether to show the onboarding-v2 demo. DECIDED ONCE, on first mount —
  // a useState initializer, never re-read from localStorage on later renders.
  // That matters: OnboardingV2 forces light mode, which re-renders AppRouter;
  // if we re-read the (now-set) flag we'd boot the user out of the demo
  // mid-flow. It only flips false via the two setStates below.
  const [showOnboardingDemo, setShowOnboardingDemo] = useState(
    () => ENABLE_ONBOARDING_V2 && !localStorage.getItem(ONBOARDING_SEEN_KEY),
  );

  // The moment we know a user is logged in, they're an established user —
  // mark onboarding as seen and never show the demo this session. This is
  // what stops an EXISTING user seeing the demo when they later log out:
  // their first logged-in app-open after this ships sets the flag for good.
  useEffect(() => {
    if (user) {
      try { localStorage.setItem(ONBOARDING_SEEN_KEY, "true"); } catch { /* storage off */ }
      setShowOnboardingDemo(false);
    }
  }, [user]);

  // Meta Ads attribution (iOS native only; no-op on web, Android and builds
  // without the Meta plugins). A logged-in user with no currency yet is a
  // brand-new account on the onboarding screen, whichever way they signed up
  // (email, Apple, Google) — log "Complete registration" once for them.
  useEffect(() => {
    if (user && !user.defaultCurrency) logMetaRegistration(user.id);
  }, [user?.id, user?.defaultCurrency]);

  // Apple's "Allow tracking?" prompt: only once they're signed in and past the
  // intro, onboarding and first-run wizard — never on the first screen.
  const readyForTrackingAsk =
    !!user && !!user.defaultCurrency && !!user.firstRunCompletedAt &&
    !(introPending && introAllowed !== false);
  useEffect(() => {
    // Then, once that's answered, Apple's native rating prompt over the app
    // (every app open; iOS itself shows it at most ~3 times a year).
    if (readyForTrackingAsk) void maybeRequestTracking().then(askForRatingOnOpen);
  }, [readyForTrackingAsk]);

  // Capture UTM params and referral codes from URL on first load.
  // Both survive the OTP step because they're stored in localStorage.
  // Also handles deferred deep-link attribution for native app installs.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const campaign = params.get("utm_campaign");
    if (campaign) localStorage.setItem("spliiit_utm_campaign", campaign);

    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("spliiit_referral_code", ref);
      // Record a fingerprint snapshot so this click can be matched after an App Store install
      recordReferralClick(ref);
    } else if (isNativeApp()) {
      // Native app opened with no ?ref= param — try to match a deferred click fingerprint.
      // If the user clicked a referral link on web before installing, this will find it.
      matchReferralClick();
    }
  }, []);

  // Sync theme from DB when user loads (cross-device consistency)
  useEffect(() => {
    if (user?.themePreference) {
      syncFromDb(user.themePreference);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Pending invite: a logged-out app user tapped "Sign up to join". Once they're
  // signed in and past currency setup, join the group for them right away — no
  // second tap, and no "create your first group" wizard in between (the accept
  // endpoint marks first-run done; we refresh the user before routing).
  const joiningRef = useRef(false);
  useEffect(() => {
    if (!user || !user.defaultCurrency || joiningRef.current) return;
    // A brand-new user (setup not finished) who opened an invite link gets
    // joined straight away too, instead of the "create your first group" wizard.
    const fromLink = !user.firstRunCompletedAt && window.location.hash.startsWith("#/invite/")
      ? extractInviteCode(window.location.hash) : null;
    const pending = getPendingInvite() ?? fromLink;
    if (!pending) return;
    joiningRef.current = true;
    void (async () => {
      const r = await acceptInvite(pending);
      clearPendingInvite();
      await refreshUser();
      if (r.ok) {
        track("group_invite_accepted", { groupId: r.groupId, via: "pending" });
        toast({ title: r.alreadyMember ? "You're already in this group" : "You're in!", description: "Taking you to the group." });
        window.location.hash = `#/groups/${r.groupId}`;
      } else {
        // Expired / revoked / gone: the invite page explains what happened.
        window.location.hash = `#/invite/${pending}`;
      }
      joiningRef.current = false;
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.defaultCurrency]);

  // Hidden preview route for onboarding v2 — bypasses all auth/onboarding gates
  // so we can dogfood the new flow without affecting production users. Reachable
  // at #/onboarding-v2-preview from any state (logged in, logged out, anything).
  // Once the new flow is ready for real users, Wave 2 wires it as the real
  // onboarding behind a feature flag — this preview route stays for QA.
  if (window.location.hash.startsWith("#/onboarding-v2-preview")) {
    return <OnboardingV2 />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" aria-label="Loading" className="animate-pulse">
            <rect width="32" height="32" rx="8" fill="hsl(30 6% 15%)" fillOpacity="0.06" />
            <circle cx="10" cy="8.8" r="1.5" fill="hsl(30 6% 15%)" />
            <path d="M10 13.6V23.8" stroke="hsl(30 6% 15%)" strokeWidth="3" strokeLinecap="round" />
            <circle cx="16" cy="8.8" r="1.5" fill="hsl(30 6% 15%)" />
            <path d="M16 13.6V23.8" stroke="hsl(30 6% 15%)" strokeWidth="3" strokeLinecap="round" />
            <circle cx="22" cy="8.8" r="1.5" fill="hsl(30 6% 15%)" />
            <path d="M22 13.6V23.8" stroke="hsl(30 6% 15%)" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <span className="text-sm text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  // Preview route for the intro tour: plays it from any state, never marks it seen.
  if (window.location.hash.startsWith("#/intro-preview")) {
    return <IntroTour member={!!user} onFinish={() => { window.location.hash = "#/"; }} />;
  }

  // Sept 2026: Spliiit lives in the app. In a normal web browser there's no
  // web app any more — only the iOS app, the Android app (TWA, always opens
  // standalone) and installed home-screen apps get past this. Admins are
  // exempt (admin dashboard) and sign in at the unlinked #/staff.
  const inApp = isIosNative || isInTWA || isStandalonePWA();
  if (import.meta.env.DEV && window.location.hash.startsWith("#/app-only-preview")) {
    return window.location.hash.includes("invite") ? <AppOnly inviteGroup="Goa trip" inviteCode="AB12CD34" /> : <AppOnly />;
  }
  if (!inApp && !user?.isAdmin) {
    const hash = window.location.hash;
    if (hash.startsWith("#/invite/")) return <InvitePage appOnly />;
    if (user) return <AppOnly />;
    if (hash.startsWith("#/reset-password")) return <ResetPassword />;
    if (hash.startsWith("#/staff")) return <AuthPage start="login" />;
    return <LandingGate />;
  }

  if (!user) {
    // Check if this is a reset-password route
    const hash = window.location.hash;
    if (hash.startsWith("#/reset-password")) {
      return <ResetPassword />;
    }
    // Public invite preview — logged-out users can see the group they were invited to
    // before being asked to sign up. The InvitePage handles the "Sign up to join" CTA itself.
    if (hash.startsWith("#/invite/")) {
      return <InvitePage />;
    }
    // First-ever open of the app on this device → show the onboarding demo.
    // showOnboardingDemo was decided once at mount; OnboardingV2 also writes
    // ONBOARDING_SEEN_KEY on mount so a future launch won't re-show it. When
    // the demo finishes, onFinish flips showOnboardingDemo false → AuthPage.
    // reset-password / invite links above are checked first so a deep link is
    // never interrupted by onboarding.
    // Intro tour, once per device, in the apps (the website has its own
    // landing page). Deep links above always win.
    if (introPending && introAllowed !== false && (isIosNative || isInTWA || isStandalonePWA())) {
      if (introAllowed === null) return null;
      return <IntroTour member={false} onFinish={(action, heard) => { rememberHeardFrom(heard); finishIntro(); setAuthStart(action); }} />;
    }
    if (showOnboardingDemo) {
      return (
        <OnboardingV2
          markSeenOnMount
          onFinish={() => setShowOnboardingDemo(false)}
        />
      );
    }
    if (authStart) return <AuthPage start={authStart} />;
    return <LandingGate />;
  }

  // Members see the intro once too, on their next open after the update.
  // Invited people included: someone who signed up from an invite link sees it
  // right after signing up, then lands on the invite to join the group.
  const hasPendingInvite = !!getPendingInvite();
  if (introPending && introAllowed !== false) {
    if (introAllowed === null) return null;
    return <IntroTour member onFinish={(_action, heard) => { rememberHeardFrom(heard); void syncHeardFrom(); finishIntro(); }} />;
  }

  // Onboarding gate — show once for new users (and legacy users with no currency set)
  if (!user.defaultCurrency) {
    return <OnboardingPreferences />;
  }

  // First-run wizard gate — shown once between onboarding and dashboard, drives the
  // empty-app → real-group activation. Existing users were backfilled on startup
  // migration so they skip this. Wizard itself POSTs /api/user/first-run + refreshes
  // user, which falls back through to the routed Layout below.
  //
  // Exception: users who signed up via an invite link already have a "first group"
  // waiting for them on the next screen — showing them the "Create your first group"
  // wizard would be redundant and confusing. Skip the wizard; the invite acceptance
  // endpoint will flip firstRunCompletedAt for them server-side. The pending-invite
  // useEffect above is about to redirect them to #/invite/<code> anyway.
  const openingInvite = window.location.hash.startsWith("#/invite/");
  if (!user.firstRunCompletedAt && !hasPendingInvite && !openingInvite) {
    return <FirstRunWizard />;
  }

  return (
    <Layout>
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/friends" component={Friends} />
        <Route path="/friends/:id">
          {(params) => <FriendDetail friendId={params.id} />}
        </Route>
        <Route path="/groups" component={Groups} />
        <Route path="/groups/:id">
          {(params) => <GroupDetail groupId={params.id} />}
        </Route>
        <Route path="/activity" component={ActivityPage} />
        <Route path="/expenses">{() => <Redirect to="/activity" />}</Route>
        <Route path="/ai" component={AiMode} />
        <Route path="/ai/:id" component={AiMode} />
        <Route path="/invite/:code" component={InvitePage} />
        <Route path="/import" component={Import} />
        {/* No in-app upgrade page (Sept 2026): everything is unlocked, and
            pricing will move to an onboarding paywall. Old links land home. */}
        <Route path="/upgrade">{() => <Redirect to="/" />}</Route>
        {user.isAdmin ? (
          <>
            <Route path="/admin" component={Admin} />
            <Route path="/admin/:section" component={Admin} />
            <Route path="/admin/:section/:resourceId" component={Admin} />
          </>
        ) : (
          <>
            <Route path="/admin">{() => <Redirect to="/" />}</Route>
            <Route path="/admin/:section">{() => <Redirect to="/" />}</Route>
            <Route path="/admin/:section/:resourceId">{() => <Redirect to="/" />}</Route>
          </>
        )}
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

// Fades out the boot splash (rendered inline in index.html) the moment the app
// is actually ready — i.e. auth has resolved and AppRouter is about to paint the
// real first screen. The minimum on-screen time + hard cap live in the inline
// script in index.html; this just fires the "app is ready" signal. Lives under
// AuthProvider so it can read the auth loading state.
function BootSplashHider() {
  const { isLoading } = useAuth();
  useEffect(() => {
    if (!isLoading) {
      (window as any).__hideBootSplash?.();
    }
  }, [isLoading]);
  return null;
}

// Tracks page views on every hash route change
function PageViewTracker() {
  const [location] = useHashLocation();
  useEffect(() => {
    trackPageView(location);
  }, [location]);
  return null;
}

function App() {
  // iOS Universal Links — register the Capacitor appUrlOpen listener once,
  // as early as possible in the app lifecycle. No-op on web/Android.
  // Mirrors the lib/iap.ts and lib/push.ts native-only init pattern.
  useEffect(() => {
    initDeepLinkHandling();
    // RevenueCat: configure on every iOS launch (not just after login) so the
    // Apple Ads attribution token is collected on first launch. No-op on
    // web/Android; login still happens later via initRevenueCat in lib/auth.
    configureRevenueCat();
    // Start the "first launch" clock for the in-app review prompt.
    recordFirstLaunch();
    // Meta SDK: pass the current App Tracking Transparency choice. Never prompts.
    syncTrackingStatus();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ThemeProvider>
          <AuthProvider>
            <BootSplashHider />
            <ForceUpdateGate>
              <Toaster />
              {CUSTOM_REVIEW_SHEET_ENABLED && <ReviewPromptSheet />}
              <PlayRatingCard />
              <Router hook={useHashLocation}>
                <PageViewTracker />
                <ErrorBoundary>
                  <AppRouter />
                </ErrorBoundary>
              </Router>
            </ForceUpdateGate>
          </AuthProvider>
        </ThemeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
